import type {
  Report,
  RevenueReport,
  Segment,
  SegmentMetric,
  SegmentsReport,
  TimePeriod,
} from '@witmetrics/utils';
import { TIME_PERIOD } from '@witmetrics/utils/constants';
import { fromBlankArray, sortNumerical } from '@witmetrics/utils/arrays';
import { API } from '@/api';
import type { LeadsPerformance, ReportByMonth } from '@/types/reports';

export function fetchSegmentsReport({
  practiceId,
  startDate,
  endDate,
  metrics,
  dimensions,
  timePeriod = TIME_PERIOD.ALL,
}: {
  practiceId: number;
  startDate: string;
  endDate: string;
  metrics: SegmentMetric[];
  dimensions: number[];
  timePeriod?: TimePeriod;
}): Promise<SegmentsReport> {
  return API.Reports.fetchSegmentsReport(practiceId, {
    startDate,
    endDate,
    timePeriod,
    filters: [],
    metrics,
    dimensions,
  });
}

// data[0] is the whole-timeframe aggregate for an ALL request.
export function getReportSegments(report: SegmentsReport): Segment[] {
  return report.data[0]?.segments ?? [];
}

export function getReportYear(report: Report) {
  return new Date(report.startDate).getFullYear();
}

const BLANK_MONTH = {
  treatmentOutstanding: 0,
  treatmentAccepted: 0,
  revenue: { currency: '', value: 0 },
};

export function groupByMonth(report: RevenueReport): LeadsPerformance[] {
  let result: ReportByMonth = new Map();
  report.data.forEach(({ startDate, leads }) => {
    const month = new Date(startDate).getMonth();
    if (!result.has(month)) {
      result.set(month, { ...leads, startDate });
    } else {
      let updates = { ...result.get(month)! };
      updates.treatmentOutstanding += leads.treatmentOutstanding;
      updates.treatmentAccepted += leads.treatmentAccepted;
      updates.revenue.value += leads.revenue.value;
      updates.startDate = startDate;
      result.set(month, updates);
    }
  });
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach((month) => {
    if (!result.has(month)) result.set(month, { ...BLANK_MONTH });
  });
  return sortNumerical(Array.from(result.keys())).map(
    (key) => result.get(key)!
  );
}

const CHART_PALETTE = [
  '#FF6B35', // orange-3
  '#4ECDC4', // teal
  '#5370F4', // purple-2
  '#10A2FD', // blue-2
  '#58BD85', // green-2
  '#F2625A', // red-2
  '#F97B19', // orange-2
  '#22D3EE', // cyan-2
  '#FB7195', // pink-2
  '#FDE047', // yellow-2
  '#234AA4', // navy-2
];

export function generateColors(length: number) {
  if (length <= CHART_PALETTE.length) {
    return CHART_PALETTE.slice(0, length);
  }
  const overflow = length - CHART_PALETTE.length;
  const extras = fromBlankArray(overflow, (i) => {
    const hue = Math.round(180 + (360 / overflow) * i);
    return `hsl(${hue}, 70%, 60%)`;
  });
  return [...CHART_PALETTE, ...extras];
}
